import {Alert, Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import i18n from '../../i18n';
import {hungarianRoundNumbers} from './hungarianRoundNumbers';
import ErrorReportingService from './ErrorReportingService';
import {resolveVatOrderType} from './orderVatResolver';
import * as vatSummary from './vatSummary';
import {showCashRegisterRetryDialog} from './CashRegisterRetryDialogService';
import {
  dequeuePendingCashRegisterReceipt,
  enqueuePendingCashRegisterReceipt,
} from './pendingCashRegisterReceipts';
import {orderNeedsInvoice} from './invoiceDetails';

/**
 * Service for generating and sending QR codes to Fiscat cashier devices
 */
class FiscatQRService {
  // VAT / ÁFA pipeline lives in the pure, dependency-free `./vatSummary` module
  // (shared with the Tauri desktop ESC/POS renderer + the isolated Jest suite).
  // These static methods stay as thin delegators to preserve the public API used
  // across the app; telemetry is forwarded via the `onInfo` sink.
  static getVATCode(vatPercentage) {
    return vatSummary.getVATCode(vatPercentage);
  }

  static roundMoney(value) {
    return vatSummary.roundMoney(value);
  }

  static applyLineDiscount(baseGross = 0, discount = '') {
    return vatSummary.applyLineDiscount(baseGross, discount);
  }

  static calculateVatSummary(saleRows = []) {
    return vatSummary.calculateVatSummary(saleRows);
  }

  static normalizePersistedVatSummary(summaryMaps = {}) {
    return vatSummary.normalizePersistedVatSummary(summaryMaps);
  }

  static getPersistedVatSummary(order = {}) {
    return vatSummary.getPersistedVatSummary(order);
  }

  static formatVatSummaryForDebug(vatSummary = []) {
    if (!Array.isArray(vatSummary) || vatSummary.length === 0) {
      return '';
    }

    const formatMoney = (value) => `${this.roundMoney(value).toFixed(2)} Ft`;

    const header = [
      i18n.t('vat_rate') || 'VAT Rate',
      i18n.t('net_amount') || 'Net Amount',
      i18n.t('vat_amount') || 'VAT Amount',
      i18n.t('gross_amount') || 'Gross Amount',
    ].join(' | ');

    const rows = vatSummary.map((summaryRow) => {
      return [
        `${summaryRow.label != null ? summaryRow.label : summaryRow.vatRate}%`,
        formatMoney(summaryRow.net),
        formatMoney(summaryRow.vat),
        formatMoney(summaryRow.gross),
      ].join(' | ');
    });

    return `${i18n.t('vat_breakdown') || 'VAT Breakdown'}\n${header}\n${rows.join('\n')}`;
  }

  static buildSaleData(order) {
    return vatSummary.buildSaleData(order, {
      onInfo: (message, meta, context) => ErrorReportingService.info(message, meta, context),
    });
  }

  static vatRowDisplayLabel(row, translate) {
    return vatSummary.vatRowDisplayLabel(row, translate);
  }

  static getVatSummary(order) {
    return vatSummary.getVatSummary(order, {
      onInfo: (message, meta, context) => ErrorReportingService.info(message, meta, context),
    });
  }

  static normalizePaymentType(type) {
    const raw = String(type || '').trim().toLowerCase();

    if (!raw) {
      return 'temporary';
    }

    if (raw === 'készpénz' || raw === 'keszpenz' || raw === 'cash') {
      return 'cash';
    }

    if (raw === 'bankkártya' || raw === 'bankkartya' || raw === 'creditcard' || raw === 'credit_card') {
      return 'creditCard';
    }

    if (raw === 'szép kártya' || raw === 'szep kartya' || raw === 'szep_card') {
      return 'szepCard';
    }

    if (raw === 'utalvány' || raw === 'utalvany' || raw === 'voucher') {
      return 'voucher';
    }

    return raw;
  }

  static isCashPaymentType(type) {
    return this.normalizePaymentType(type) === 'cash';
  }

  static resolvePaymentTypeValue(payment = {}) {
    return payment?.paymentType || payment?.type || payment?.method || payment?.name || payment?.payment_method || '';
  }

  static shouldApplyServiceFee(order = {}) {
    return resolveVatOrderType(order) === 'dineIn';
  }

  static async hasConfiguredCashierDevice() {
    try {
      const paymentCashier = await AsyncStorage.getItem('cashierDevice');
      if (!paymentCashier) {
        return false;
      }

      const parsedCashier = JSON.parse(paymentCashier);
      if (typeof parsedCashier === 'string') {
        return Boolean(parsedCashier);
      }

      return Boolean(parsedCashier?.address || parsedCashier?.id || parsedCashier?.name);
    } catch (_error) {
      return false;
    }
  }

  static async confirmCashRegisterPrint(options = {}) {
    const {
      title = i18n.t('print') || 'Nyomtatás',
      message = i18n.t('cash_register_print_prompt'),
      confirmText = i18n.t('print_plus_cash_register'),
      skipText = i18n.t('print_only'),
    } = options;

    if (!(await this.hasConfiguredCashierDevice())) {
      return false;
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.confirm === 'function') {
      return window.confirm(`${message}\n\nOK = ${confirmText}\nMégse = ${skipText}`);
    }

    return new Promise((resolve) => {
      Alert.alert(
        title,
        message,
        [
          {
            text: skipText,
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: confirmText,
            onPress: () => resolve(true),
          },
        ],
        {
          cancelable: true,
          onDismiss: () => resolve(false),
        }
      );
    });
  }

  static emitWebCashierDebugPayload(qrCodeString, order = {}) {
    if (Platform.OS !== 'web') {
      return;
    }

    const orderCode = order?.orderCode || 'UNKNOWN';
    const vatSummary = this.getVatSummary(order);
    const formattedVatSummary = this.formatVatSummaryForDebug(vatSummary);
    const debugMessage = `[Fiscat Debug] Order ${orderCode}\n\nPayload:\n${qrCodeString}${formattedVatSummary ? `\n\n${formattedVatSummary}` : ''}`;

    // Keep the payload visible for web debugging even when no cashier is configured.
    console.log(debugMessage);

    if (typeof window !== 'undefined' && typeof window.alert === 'function') {
      window.alert(debugMessage);
    }
  }

  static async sendQRCodeStringToCashier(qrCodeString, order = {}) {
    const paymentCashier = await AsyncStorage.getItem('cashierDevice');

    if (!paymentCashier) {
      return true;
    }

    // Route the payload over the configured transport: Bluetooth (Android),
    // WiFi/TCP, or an on-screen QR the register scans. iOS/iPad — which cannot
    // open a Bluetooth Classic SPP link — is served by the WiFi and QR paths.
    const { dispatchCashierPayload } = require('./cashierTransport');
    const result = await dispatchCashierPayload(qrCodeString, {
      orderCode: order?.orderCode,
    });

    if (result?.skipped) {
      return true;
    }

    if (result?.ok === true) {
      return true;
    }

    throw new Error('Cashier device is not configured correctly');
  }

  /**
   * Generate a QR code string for a Fiscat cashier device
   * @param {Object} order - The order object
   * @returns {string} - The generated QR code string
   */
  static generateQRCodeString(order) {
    if (!order) {
      return null;
    }

    const {orderCode, paymentTypes, orderSum, paymentType} = order;
    const saleData = this.buildSaleData(order);
    if (!saleData) {
      return null;
    }

    const {rowsCount, qrCodeString, vatSummary} = saleData;

    // Create the full QR code string
    // Format: fiscat/AEE|SLD|CashierID|ReceiptID|FooterCount|Footer1|Footer2|Footer3|ItemCount|...
    const normalizedOrderCode = String(orderCode || 'UNKNOWN');
    const cleanOrderCode = normalizedOrderCode.replace('-', '');

    // Keep footer structure identical to the known-working integration.
    let fullQRCode = `fiscat/AEE|SLD||${cleanOrderCode}|6|Rendelés száma:|#${normalizedOrderCode}|-------------|www.happygastro.hu|Üzemelteti a|Happy Solutions||||${rowsCount}|${qrCodeString}`;

    // Add payment types and amounts
    if (paymentTypes && paymentTypes.length > 0) {
      paymentTypes.forEach((payment) => {
        const resolvedPaymentType = this.resolvePaymentTypeValue(payment);
        const isCash = this.isCashPaymentType(resolvedPaymentType);
        const paymentCode = isCash ? 'P' : 'N';
        const paymentAmount = Math.round(payment.amount || 0);
        const roundedAmount = isCash ? hungarianRoundNumbers(paymentAmount) : paymentAmount;

        fullQRCode += `${paymentCode}|${roundedAmount}`;
      });
    } else {
      const isCash = this.isCashPaymentType(paymentType);
      const paymentTypeCode = isCash ? 'P' : 'N';
      const paymentAmount = Math.round(orderSum || 0);
      fullQRCode += `${paymentTypeCode}|${isCash ? hungarianRoundNumbers(paymentAmount) : paymentAmount}`;
    }

    // QR code data logging has been removed as per requirements
    ErrorReportingService.info('Fiscat VAT summary calculated', {
      orderCode,
      vatSummary,
    }, 'FiscatQRService.generateQRCodeString');

    return fullQRCode;
  }


  /**
   * Send a QR code to the cashier device
   * @param {Object} order - The order object
   * @param {Function} sendData - Function to send data to the device
   * @returns {Promise<boolean>} - Whether the operation was successful
   */
  static async sendToCashier(order, sendData) {
    try {
      // When the customer requested a VAT invoice the order is billed via a NAV
      // invoice (issued by the backend) — it must NEVER be sent to a fiscal cash
      // register. Skip silently and report success so callers don't retry.
      if (orderNeedsInvoice(order)) {
        ErrorReportingService.info(
          "Skipping cash register send — order requires a VAT invoice",
          { orderCode: order?.orderCode },
          "FiscatQRService.sendToCashier"
        );
        return true;
      }

      // Allow callers (e.g. the "re-send" action from the order details
      // screen) to pass a previously generated QR payload on the order so
      // that the *exact* same receipt is re-sent rather than rebuilt.
      const qrCodeString = order?.cashRegister?.payload
        || order?._cashRegisterPayload
        || this.generateQRCodeString(order);


      // If qrCodeString is null, it means the order was empty or had zero price
      if (!qrCodeString) {
        ErrorReportingService.info("Skipping empty or zero-price order", { orderCode: order.orderCode }, "FiscatQRService.sendToCashier");
        return true; // Resolve successfully since we're intentionally skipping
      }

      //this.emitWebCashierDebugPayload(qrCodeString, order);

      ErrorReportingService.info("QR code generated", { qrCode: qrCodeString, hasLegacySender: typeof sendData === 'function' }, "FiscatQRService.sendToCashier");

      const orderIdentity = {
        orderId: order?._id || order?.id || null,
        orderCode: order?.orderCode || null,
      };

      const attemptSendOriginalReceipt = () => this.sendQRCodeStringToCashier(qrCodeString, order);

      try {
        await attemptSendOriginalReceipt();
        // Successful send – clear any previously deferred receipt.
        try { await dequeuePendingCashRegisterReceipt(orderIdentity); } catch (_) { /* ignore */ }
        return true;
      } catch (initialError) {
        ErrorReportingService.error(
          'Initial cashier send failed; showing retry dialog',
          { error: initialError, orderCode: order?.orderCode },
          'FiscatQRService.sendToCashier'
        );

        // Persist the payload so the operator can re-send later from the
        // order details screen even if they dismiss the dialog now.
        try {
          await enqueuePendingCashRegisterReceipt({
            ...orderIdentity,
            qrCodeString,
            lastError: String(initialError?.message || initialError || ''),
          });
        } catch (_) { /* best effort */ }

        // The operator explicitly deferred the on-screen QR (WiFi/iOS path):
        // the payload is already persisted for re-send, so do NOT escalate to
        // the Bluetooth-flavoured retry dialog — treat it as a soft success.
        if (initialError?.deferred) {
          return true;
        }

        const retryResult = await showCashRegisterRetryDialog({
          orderCode: order?.orderCode,
          initialError,
          attemptRetry: attemptSendOriginalReceipt,
        });

        if (retryResult === true) {
          try { await dequeuePendingCashRegisterReceipt(orderIdentity); } catch (_) { /* ignore */ }
          return true;
        }

        const finalError = new Error('Cash register not responding after receipt retry');
        finalError.cause = initialError;
        finalError.context = {
          orderCode: order?.orderCode,
          originalErrorMessage: initialError?.message,
        };
        ErrorReportingService.error("Error sending to cashier", { error: finalError }, "FiscatQRService.sendToCashier");
        await ErrorReportingService.reportError(finalError, { source: "FiscatQRService.sendToCashier" });
        return Promise.reject(finalError);
      }
    } catch (e) {
      ErrorReportingService.error("Error sending to cashier", { error: e }, "FiscatQRService.sendToCashier");
      await ErrorReportingService.reportError(e, { source: "FiscatQRService.sendToCashier" });
      return Promise.reject(e);
    }
  }

  /**
   * Show a retry dialog for failed cashier communication
   * @param {Function} attemptSendToCashier - Function to retry sending to cashier
   * @returns {Promise<boolean>} - Whether the operation was successful
   */
  static showRetryDialog(attemptSendToCashier) {
    return showCashRegisterRetryDialog({
      initialError: new Error(i18n.t('cashier_communication_failed') || 'Cashier communication failed'),
      attemptRetry: attemptSendToCashier,
    });
  }

}

export default FiscatQRService;
